import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect, Fragment } from 'react';
import { Transition } from '@headlessui/react';
import { CogIcon, LogoutIcon, ShieldCheckIcon, UserCircleIcon, BeakerIcon, BellIcon, QuestionMarkCircleIcon, CurrencyDollarIcon } from '@heroicons/react/outline';
import { setupAndSeedDatabase } from '../../services/contractService';
import { toast } from 'react-hot-toast';

import useAuthStore from '../../store/authStore';
import AppHeader from './AppHeader';
import BottomNavBar from './BottomNavBar';

const DashboardLayout = () => {
  const { userData, logout, isLoading } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [isNavigating, setIsNavigating] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    setIsNavigating(true);
    const timer = setTimeout(() => setIsNavigating(false), 300);
    return () => clearTimeout(timer);
  }, [location]);

  const handleSignOut = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  if (isLoading || isNavigating) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  const sidebarNav = [
      { name: 'الإدارة المالية', href: '/finance', icon: CurrencyDollarIcon, roles: ['lawyer'] },
      { name: 'الإعدادات', href: '/settings', icon: CogIcon },
      { name: 'الإشعارات', href: '/notifications', icon: BellIcon },
      { name: 'المساعدة والدعم', href: '/help', icon: QuestionMarkCircleIcon },
  ].filter(item => !item.roles || (userData?.role && item.roles.includes(userData.role)));

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Sliding sidebar menu */}
      <Transition.Root show={mobileMenuOpen} as={Fragment}>
        <div className="fixed inset-0 z-40 flex" >
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setMobileMenuOpen(false)} />
          </Transition.Child>
          <Transition.Child
            as={Fragment}
            enter="transition ease-in-out duration-300 transform"
            enterFrom="translate-x-full"
            enterTo="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leaveFrom="translate-x-0"
            leaveTo="translate-x-full"
          >
            <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white">
              <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
                <div className="flex-shrink-0 flex items-center px-4 mb-8">
                    <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-medium text-xl">
                      {userData?.fullName ? userData.fullName.charAt(0).toUpperCase() : 'U'}
                    </div>
                    <div className="ml-0 mr-4">
                      <p className="text-base font-bold text-gray-900">
                        {userData?.fullName || 'User'}
                      </p>
                      <Link to="/profile" className="text-sm font-medium text-blue-600 hover:text-blue-800">
                        عرض الملف الشخصي
                      </Link>
                    </div>
                </div>


                <nav className="mt-6 px-2 space-y-1">
                    {sidebarNav.map((item) => (
                        <Link
                        key={item.name}
                        to={item.href}
                        className='text-gray-600 hover:bg-gray-50 hover:text-gray-900 group flex items-center px-2 py-2 text-base font-medium rounded-md'
                        >
                        <item.icon className="ml-4 mr-0 flex-shrink-0 h-6 w-6 text-gray-400 group-hover:text-gray-500" aria-hidden="true" />
                        {item.name}
                        </Link>
                    ))}
                </nav>
              </div>
              <div className="flex-shrink-0 flex border-t border-gray-200 p-4">
                <button
                  onClick={handleSignOut}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0.5rem 1rem',
                    border: '1px solid transparent',
                    borderRadius: '0.375rem',
                    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: '#ffffff',
                    backgroundColor: 'oklch(0.55 0.24 262.91)',
                    transition: 'background-color 0.2s',
                    cursor: 'pointer',
                    outline: 'none'
                  }}
                  onMouseOver={e => e.currentTarget.style.backgroundColor = 'oklch(0.5 0.24 262.91)'}
                  onMouseOut={e => e.currentTarget.style.backgroundColor = 'oklch(0.55 0.24 262.91)'}
                  onFocus={e => e.currentTarget.style.boxShadow = '0 0 0 2px rgba(59, 130, 246, 0.5)'}
                  onBlur={e => e.currentTarget.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)'}
                >
                  <LogoutIcon style={{ marginLeft: '0.5rem', marginRight: '-0.25rem', height: '1.25rem', width: '1.25rem' }} aria-hidden="true" />
                  تسجيل الخروج
                </button>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Transition.Root>

      {/* Main content with Header */}
      <AppHeader onMenuClick={() => setMobileMenuOpen(true)} />
      <main className="flex-1 overflow-y-auto">
        <Outlet context={{ openMenu: () => setMobileMenuOpen(true) }} />
      </main>

      {/* Bottom Navigation */}
      <BottomNavBar />
    </div>
  );
};

export default DashboardLayout;
